/**
 * Migration from kinklist-v2 data (SPEC §4.2): the JSON embedded in v2 images
 * (`{ username, categories, ratings }`, ratings identified by name).
 */
import * as z from 'zod/mini';

import { unsetRating } from '../model/access';
import { normalizeColor, normalizeComment, normalizeName, normalizeTitle } from '../model/text';
import type { Category, KinklistDoc, Rating } from '../model/types';
import { LIMITS } from '../model/types';
import { ImportError } from './errors';
import type { HydrateContext } from './wire';

const v2Schema = z.object({
  username: z.optional(z.string()),
  ratings: z.array(z.object({ name: z.string(), color: z.string() })),
  categories: z.array(
    z.object({
      name: z.string(),
      subcategories: z.array(z.string()),
      kinks: z.array(
        z.object({
          name: z.string(),
          ratings: z.optional(z.record(z.string(), z.string())),
          comment: z.nullish(z.string()),
        }),
      ),
    }),
  ),
});

export type V2Data = z.input<typeof v2Schema>;

/** Whether `data` has the v2 shape (no `v` field, v2 keys). Cheap pre-check for dispatch. */
export function looksLikeV2(data: unknown): boolean {
  return (
    typeof data === 'object' &&
    data !== null &&
    !('v' in data) &&
    'categories' in data &&
    'ratings' in data
  );
}

/**
 * Maps v2 data to a v3 document:
 * - ratings get ids; colors are normalized to `#RRGGBB` (invalid colors become white);
 * - selections of unknown rating names and missing role entries become `ratings[0]`;
 * - blank names get a placeholder, kinks with blank names are dropped (v2 deleted them);
 * - roles are limited to 1–4 (a category without any gets "General").
 */
export function fromV2(input: unknown, ctx: HydrateContext): KinklistDoc {
  const parsed = v2Schema.safeParse(input);
  if (!parsed.success) throw new ImportError('invalid', z.prettifyError(parsed.error));
  const data = parsed.data;

  const ratings: Rating[] = data.ratings.map((rating, i) => ({
    id: ctx.newId(),
    name: normalizeName(rating.name) ?? `Rating ${String(i + 1)}`,
    color: normalizeColor(rating.color) ?? '#FFFFFF',
  }));
  if (ratings.length < LIMITS.minRatings) {
    throw new ImportError('invalid', 'A list needs at least two ratings');
  }
  const unsetId = unsetRating(ratings).id;
  // v2 identifies ratings by name; the first rating with a name wins.
  const byName = new Map<string, string>();
  data.ratings.forEach((rating, i) => {
    const id = ratings[i]?.id;
    if (id !== undefined && !byName.has(rating.name)) byName.set(rating.name, id);
  });

  const categories = data.categories.map((category, ci): Category => {
    const subcategories = category.subcategories.slice(0, LIMITS.maxRoles);
    const roleNames = subcategories.length > 0 ? subcategories : ['General'];
    const roles = roleNames.map((name, i) => ({
      id: ctx.newId(),
      name: normalizeName(name) ?? `Role ${String(i + 1)}`,
    }));
    return {
      id: ctx.newId(),
      name: normalizeName(category.name) ?? `Category ${String(ci + 1)}`,
      roles,
      kinks: category.kinks.flatMap((kink) => {
        const name = normalizeName(kink.name);
        if (name === null) return [];
        const selections = Object.fromEntries(
          roles.map((role, i) => {
            const ratingName = kink.ratings?.[roleNames[i] ?? ''];
            return [role.id, byName.get(ratingName ?? '') ?? unsetId];
          }),
        );
        const comment = normalizeComment(kink.comment ?? '');
        const result = { id: ctx.newId(), name, ratings: selections };
        return [comment === undefined ? result : { ...result, comment }];
      }),
    };
  });

  return {
    v: 3,
    title: normalizeTitle(data.username ?? ''),
    ratings,
    categories,
    updatedAt: ctx.now().toISOString(),
  };
}
